/**
 * Is this post AI-made? Used by the "hide AI images" filter. Sources tag it many ways, so this checks the tag list for the
 * common spellings and for the names of the tools, and treats posts from sources that are AI-only as AI.
 *
 * Tags are compared whole (see lib/blacklist-match.ts for why substring matching was a bug there): `ai` and `ai_art` count,
 * `maid` and `long_hair` do not.
 */

const AI_TAGS = new Set([
  "ai",
  "ai_generated",
  "ai_assisted",
  "ai_created",
  "ai_content",
  "ai_art",
  "ai_artwork",
  "ai_image",
  "ai_images",
  "ai_edited",
  "ai_upscaled",
  "ai_voice",
  "ai_hands",
  "by_ai",
  "made_with_ai",
  "created_with_ai",
  "created_by_ai",
  "generated",
  "generated_by_ai",
  "artificial_intelligence",
  "neural_network",
  "machine_learning",
  "synthetic",
  // tools and models
  "stable_diffusion",
  "stablediffusion",
  "sdxl",
  "sd_xl",
  "novelai",
  "nai_diffusion",
  "nai3",
  "midjourney",
  "dall_e",
  "dalle",
  "comfyui",
  "automatic1111",
  "pony_diffusion",
  "ponyxl",
  "pony_xl",
  "flux_ai",
  "civitai",
  "lora_generated",
  "diffusion",
])

/** Sources whose posts are AI-made as a rule. */
const AI_SITES = new Set(["civitai"])

function normalize(tag: string): string {
  return tag
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
}

export interface AiCheckable {
  site?: string | null
  tags?: readonly string[] | null
}

export function isAiPost(post: AiCheckable): boolean {
  if (post.site && AI_SITES.has(post.site.toLowerCase())) return true
  for (const raw of post.tags ?? []) {
    const tag = normalize(raw)
    if (AI_TAGS.has(tag)) return true
    // "ai_generated_(...)" style or "ai:..." namespaced forms
    if (tag.startsWith("ai_generated") || tag.startsWith("ai_assisted") || tag.startsWith("ai:")) return true
    // a title used as a tag ("AI generated girl in a maid outfit"): the words "ai generated" / "ai art" / "by ai" in it
    if (/\s/.test(raw.trim()) && /\b(ai[ _-]?(generated|art|assisted|image|created)|by ai|made with ai|stable diffusion|midjourney|novelai)\b/i.test(raw)) return true
  }
  return false
}
